import { Menu, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState, type ReactNode } from "react";

import { cn } from "../../lib/utils.js";

export interface NavItem {
  key: string;
  label: ReactNode;
  icon?: LucideIcon;
  href?: string;
  active?: boolean;
  badge?: ReactNode;
  onClick?: () => void;
}

export interface NavSection {
  title?: ReactNode;
  items: NavItem[];
}

export interface AppShellProps {
  brand?: ReactNode;
  sections: NavSection[];
  /** Render at the bottom of the sidebar — typically <UserMenu />. */
  sidebarFooter?: ReactNode;
  /** Optional topbar on the right (search, notifications, etc). */
  topbar?: ReactNode;
  children: ReactNode;
  className?: string;
}

const NavLink = ({
  item,
  onNavigate,
}: {
  item: NavItem;
  /** Fires after the item's own handler — closes the mobile drawer. */
  onNavigate?: () => void;
}) => {
  const Icon = item.icon;
  const inner = (
    <>
      {Icon ? <Icon className="size-4 shrink-0" aria-hidden /> : null}
      <span className="flex-1 truncate">{item.label}</span>
      {item.badge ? <span className="ml-auto">{item.badge}</span> : null}
    </>
  );
  const className = cn(
    "group flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors",
    "text-muted-foreground hover:bg-accent hover:text-foreground",
    item.active && "bg-accent text-foreground"
  );
  if (item.href) {
    return (
      <a
        href={item.href}
        className={className}
        aria-current={item.active ? "page" : undefined}
        onClick={onNavigate}
      >
        {inner}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        item.onClick?.();
        onNavigate?.();
      }}
      className={cn(className, "w-full text-left")}
      aria-current={item.active ? "page" : undefined}
    >
      {inner}
    </button>
  );
};

const SidebarNav = ({
  sections,
  onNavigate,
}: {
  sections: NavSection[];
  onNavigate?: () => void;
}) => (
  <nav className="flex-1 overflow-y-auto px-2 py-4">
    {sections.map((section, i) => (
      <div key={i} className={cn(i > 0 && "mt-6")}>
        {section.title ? (
          <p className="px-2.5 pb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {section.title}
          </p>
        ) : null}
        <ul className="grid gap-0.5">
          {section.items.map((item) => (
            <li key={item.key}>
              <NavLink item={item} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      </div>
    ))}
  </nav>
);

export const AppShell = ({
  brand,
  sections,
  sidebarFooter,
  topbar,
  children,
  className,
}: AppShellProps) => {
  // Mobile slide-over drawer — the sidebar is hidden below `md`.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = () => setDrawerOpen(false);

  return (
    <div className={cn("min-h-screen bg-background", className)}>
      <div className="mx-auto grid min-h-screen w-full max-w-[1440px] grid-cols-1 md:grid-cols-[260px_1fr]">
        <aside className="sticky top-0 hidden h-screen flex-col border-r bg-card/50 backdrop-blur md:flex">
          {brand ? (
            <div className="flex h-14 items-center border-b px-4 text-sm font-semibold tracking-tight">
              {brand}
            </div>
          ) : null}
          <SidebarNav sections={sections} />
          {sidebarFooter ? (
            <div className="border-t p-2">{sidebarFooter}</div>
          ) : null}
        </aside>

        {/* Mobile drawer + backdrop. Rendered only while open. */}
        {drawerOpen ? (
          <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={closeDrawer}
              aria-hidden
            />
            <div className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col border-r bg-card shadow-xl">
              <div className="flex h-14 items-center justify-between border-b px-4 text-sm font-semibold tracking-tight">
                <span className="truncate">{brand}</span>
                <button
                  type="button"
                  onClick={closeDrawer}
                  aria-label="Close navigation"
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  <X className="size-5" />
                </button>
              </div>
              <SidebarNav sections={sections} onNavigate={closeDrawer} />
              {sidebarFooter ? (
                <div className="border-t p-2">{sidebarFooter}</div>
              ) : null}
            </div>
          </div>
        ) : null}

        <main className="flex min-h-screen min-w-0 flex-col">
          {/* Mobile header: hamburger + brand. Hidden from `md` up. */}
          <header className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b bg-background/80 px-4 backdrop-blur md:hidden">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open navigation"
              className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <Menu className="size-5" />
            </button>
            <div className="min-w-0 flex-1 truncate text-sm font-semibold tracking-tight">
              {brand}
            </div>
            {topbar}
          </header>
          {topbar ? (
            <header className="sticky top-0 z-30 hidden h-14 items-center justify-end gap-3 border-b bg-background/80 px-6 backdrop-blur md:flex">
              {topbar}
            </header>
          ) : null}
          <div className="min-w-0 flex-1 px-4 py-6 sm:px-6">{children}</div>
        </main>
      </div>
    </div>
  );
};
